import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { WarrantiesListPage as SharedWarrantiesListPage } from '@/features/warranties/components/WarrantiesListPage';
import { Can } from '../../../components/Can';
import { useCurrentUserWording } from '../../../hooks/useVendorWording';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { useIsWarrantyPolicyOwner } from '@/features/warranties/hooks/useIsWarrantyPolicyOwner';
import { useAvailableSocieties } from '../../../hooks/societies/useAvailableSocieties';
import { EligibleSocietySelector } from '@/features/societies/components/EligibleSocietySelector';

export const WarrantiesListPage = () => {
  const navigate = useNavigate();
  const wording = useCurrentUserWording();
  const [total, setTotal] = useState<number | undefined>();
  const { currentUser } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Un user rattaché à plusieurs sociétés portant chacune sa stratégie (ex. tête
  // de groupe + agence décentralisée) choisit celle qu'il consulte. La société
  // retenue est lue dans l'URL et n'est acceptée que si elle figure parmi les
  // stratégies proposées ; sinon on retombe sur la société principale.
  const { societies: strategySocieties } = useAvailableSocieties('warrantyPolicy');
  const requestedSociety = strategySocieties.find((s) => s.societyId === searchParams.get('societyId'));
  const selectedSocietyId = requestedSociety?.societyId ?? currentUser?.societyId;
  const showSocietySelector = strategySocieties.length > 1;
  const isWarrantyPolicyOwner = useIsWarrantyPolicyOwner(selectedSocietyId);

  const handleSocietyChange = (societyId: string | undefined) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      if (societyId) next.set('societyId', societyId);
      else next.delete('societyId');
      next.delete('page');
      return next;
    });
  };

  const pluralize = (count: number) => count > 1 ? 'éléments' : 'élément';

  return (
    <div>
      {/* Header */}
      <div className="tw-pt-4 tw-mb-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3">
            <Shield className="tw-w-8 tw-h-8 tw-text-affinicar-primary" />
            <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">
              Ma stratégie de {wording.pluralLower}
              {total !== undefined && (
                <span className="tw-ml-1 tw-text-base tw-font-normal tw-text-gray-400">
                  ({total} {pluralize(total)})
                </span>
              )}
            </h1>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            {showSocietySelector && (
              <div className="tw-w-64">
                <EligibleSocietySelector
                  action="warrantyPolicy"
                  value={selectedSocietyId}
                  onChange={handleSocietyChange}
                  size="sm"
                  dataTestId="warranties-society-selector"
                />
              </div>
            )}
            <Can permission="warranty.manage_recommendations">
              <button
                onClick={() => navigate('/warranties/presets')}
                className="tw-px-4 tw-py-2 tw-bg-white tw-text-affinicar-primary tw-border tw-border-affinicar-primary tw-rounded-lg hover:tw-bg-gray-50 tw-transition tw-font-medium"
              >
                Presets
              </button>
            </Can>
            {isWarrantyPolicyOwner && (
              <Can permission="warranty.create">
                <button
                  onClick={() => navigate(showSocietySelector && selectedSocietyId ? `/warranties/create?societyId=${selectedSocietyId}` : '/warranties/create')}
                  className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition tw-font-medium"
                >
                  + Nouvelle {wording.singularLower}
                </button>
              </Can>
            )}
          </div>
        </div>
      </div>

      <SharedWarrantiesListPage
        onRowClick={(uuid) => navigate(`/warranties/${uuid}`)}
        hideTitle
        embedded
        onTotalChange={setTotal}
        scopeSocietyId={selectedSocietyId}
      />
    </div>
  );
};
